import apiClient from './index'
import TokenService from './tokenService'

const setupInterceptors = (acquireToken) => {
    console.log('Setting up Interceptors...')

    apiClient.interceptors.request.use(request => {
        return new Promise(function (resolve) {
            const token = TokenService.getLocalAccessToken()
            if (token) {
                request.headers.Authorization = `Bearer ${token}`
                //request.headers.AccessToken = token

                const activeUserAccount = localStorage.activeUserAccount
                const activeCountryAccount = localStorage.activeCountryAccount

                if (activeUserAccount) {
                    request.headers.AccountId = activeUserAccount
                    request.headers.CountryAccountId = activeCountryAccount
                }
            }

            request.headers.ClientTimezone = new Date().toString().match(/([A-Z]+[+-][0-9]+.*)/)[1]

            resolve(request)
        })
    })

    // Track if we're currently refreshing to prevent multiple refresh attempts
    let isRefreshing = false;
    let refreshSubscribers = [];

    function subscribeTokenRefresh(cb) {
        refreshSubscribers.push(cb);
    }

    function onTokenRefreshed(token) {
        refreshSubscribers.forEach(cb => cb(token));
        refreshSubscribers = [];
    }

    apiClient.interceptors.response.use( 
        undefined, // Response
        
        async (err) => {
            const originalConfig = err.config;

            if (/*originalConfig.url !== "/auth/signin" && */err.response) {
                // Access Token was expired
                if (err.response.status === 401 && !originalConfig._retry) {
                    
                    // If we're already refreshing, queue this request
                    if (isRefreshing) {
                        return new Promise((resolve) => {
                            subscribeTokenRefresh((token) => {
                                if (token) {
                                    originalConfig.headers.Authorization = `Bearer ${token}`;
                                    resolve(apiClient(originalConfig));
                                } else {
                                    resolve(Promise.reject(new Error('Authentication failed')));
                                }
                            });
                        });
                    }

                    originalConfig._retry = true;
                    isRefreshing = true;

                    try {
                        console.log('Access Token expired. Acquiring new...')

                        const result = await acquireToken()
                        
                        // Poll for the token with a timeout
                        // The MSAL event callback updates localStorage asynchronously
                        // This can take longer than expected, especially after long inactivity
                        const maxWaitTime = 10000; // 10 seconds max (increased for redirect cases)
                        const pollInterval = 200; // Check every 200ms
                        let elapsedTime = 0;
                        
                        while (elapsedTime < maxWaitTime) {
                            const newToken = TokenService.getLocalAccessToken();
                            
                            // Check if we have a valid token in localStorage (set by MSAL event callback)
                            if (newToken && result.value?.accessToken) {
                                console.log('Token refreshed successfully via event callback, retrying original request...')
                                isRefreshing = false;
                                onTokenRefreshed(newToken);
                                return apiClient(originalConfig);
                            }
                            
                            // Check if we have the token directly in the result
                            if (result.value?.accessToken) {
                                const token = result.value.accessToken;
                                // Manually update if event callback hasn't fired yet
                                TokenService.updateLocalAccessToken(token)
                                console.log('Token refreshed (manual update), retrying original request...')
                                isRefreshing = false;
                                onTokenRefreshed(token);
                                return apiClient(originalConfig);
                            }
                            
                            // Wait before checking again
                            await new Promise(resolve => setTimeout(resolve, pollInterval));
                            elapsedTime += pollInterval;
                        }
                        
                        // If we get here, we timed out waiting for the token
                        // This likely means a redirect is happening or will happen
                        console.warn('Token refresh timed out after', maxWaitTime, 'ms. A redirect may be in progress.')
                        isRefreshing = false;
                        onTokenRefreshed(null);
                        // Don't reject here - let the redirect complete
                        // The page will reload with a fresh token after redirect
                        return new Promise(() => {}); // Return a never-resolving promise to keep the request pending during redirect
                    } catch (_error) {
                        console.error('Token refresh error:', _error)
                        isRefreshing = false;
                        onTokenRefreshed(null);
                        return Promise.reject(new Error('Authentication failed'));
                    }
                }
            }

            return Promise.reject(err?.response?.data?.Message ?? err);
        }
    )
}

export default setupInterceptors